import { useState } from 'react'
import { CreditCard, RefreshCw, CheckCircle2, XCircle } from 'lucide-react'
import { getAdminPayments, adminConfirmPayment, adminRejectPayment } from '../../services/api'
import {
  C, Card, Row, Btn, Chips, Loading, ErrorLine, Empty, inputStyle, labelStyle,
  useAsync, fmtDateTime, fmtMoney, userLabel, Pill, divider, confirmAsync, NETWORK_LABELS,
} from './adminUI'

// Раздел «Оплаты»: все заявки на оплату с фильтром по статусу.
// Для pending-заявок можно вручную подтвердить (тогда бэкенд помечает заявку
// оплаченной И активирует подписку — иначе юзер заплатил, а доступа нет) или
// отклонить (адрес возвращается в пул свободных).

const STATUS = {
  pending:   { label: 'Ожидает',   color: C.warn },
  paid:      { label: 'Оплачено',  color: C.accent },
  expired:   { label: 'Истекла',   color: C.dim },
  failed:    { label: 'Отклонена', color: C.red },
  cancelled: { label: 'Отменена',  color: C.dim },
}

export default function PaymentsSection() {
  const [status, setStatus] = useState('pending')
  const [openId, setOpenId] = useState(null)
  const [hash, setHash] = useState('')
  const [busy, setBusy] = useState(0)
  const [note, setNote] = useState('')

  const { data, loading, error, reload } = useAsync(
    () => getAdminPayments({ status, limit: 80 }),
    [status],
  )

  const by = data?.byStatus || {}
  const chips = [
    { key: 'pending', label: 'Ожидают', count: by.pending || 0 },
    { key: 'paid',    label: 'Оплачены', count: by.paid || 0 },
    { key: 'failed',  label: 'Отклонены', count: by.failed || 0 },
    { key: 'expired', label: 'Истекли', count: by.expired || 0 },
    { key: 'all',     label: 'Все' },
  ]

  const confirm = async (row) => {
    if (!(await confirmAsync(
      `Подтвердить оплату #${row.id} на ${row.amount_usdt} USDT и выдать подписку «${row.plan_title}» юзеру ${row.user_id}?`
    ))) return
    setBusy(row.id); setNote('')
    try {
      const r = await adminConfirmPayment(row.id, hash.trim() || undefined)
      setNote(r?.subscription?.expiresAt
        ? `Заявка #${row.id} подтверждена, подписка до ${r.subscription.expiresAt}`
        : `Заявка #${row.id} подтверждена`)
      setHash('')
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(0) }
  }

  const reject = async (row) => {
    if (!(await confirmAsync(`Отклонить заявку #${row.id}? Адрес вернётся в пул.`))) return
    setBusy(row.id); setNote('')
    try {
      await adminRejectPayment(row.id)
      setNote(`Заявка #${row.id} отклонена`)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(0) }
  }

  const rows = data?.rows || []

  return (
    <div>
      <Chips options={chips} value={status} onChange={setStatus} />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ flex: 1, fontSize: 11.5, color: C.dim }}>
          {loading ? 'Загрузка…' : `Заявок: ${rows.length}`}
        </div>
        <Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>
      </div>

      <ErrorLine onRetry={reload}>{error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {loading && !rows.length ? <Loading /> : !rows.length ? <Empty>Заявок нет</Empty> : (
        <Card style={{ padding: '6px 14px' }}>
          {rows.map((r, i) => {
            const st = STATUS[r.status] || { label: r.status, color: C.muted }
            const open = openId === r.id
            return (
              <div key={r.id} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
                <div
                  onClick={() => { setOpenId(open ? null : r.id); setHash('') }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
                      {fmtMoney(r.amount_usdt)} USDT · {r.plan_title}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                      #{r.id} · {userLabel(r)} (id {r.user_id}) · {fmtDateTime(r.created_at)}
                    </div>
                  </div>
                  <Pill color={st.color}>{st.label}</Pill>
                </div>

                {open && (
                  <div style={{ marginTop: 8, background: C.inner, borderRadius: 12, padding: '8px 12px' }}>
                    <Row label="Сеть" value={NETWORK_LABELS[r.network] || r.network} />
                    <Row label="К оплате" value={`${r.amount_coin ?? '—'} ${r.currency || ''}`} mono />
                    <Row label="Адрес" value={r.deposit_address || '—'} mono />
                    <Row label="Создана" value={fmtDateTime(r.created_at)} />
                    <Row label="Действует до" value={fmtDateTime(r.expires_at)} />
                    {r.paid_at && <Row label="Оплачена" value={fmtDateTime(r.paid_at)} valueColor={C.accent} />}
                    {r.tx_hash && <Row label="Хэш" value={r.tx_hash} mono />}
                    {r.confirmed_amount != null && <Row label="Пришло" value={`${r.confirmed_amount}`} mono />}
                    <Row label="Подписка выдана" value={r.activated ? 'да' : 'нет'} valueColor={r.activated ? C.accent : C.dim} />

                    {r.status === 'pending' && (
                      <>
                        <div style={divider} />
                        <div style={labelStyle}>Хэш транзакции (необязательно)</div>
                        <input
                          value={hash}
                          onChange={e => setHash(e.target.value)}
                          placeholder="0x… / TRC-20 txid"
                          style={{ ...inputStyle, marginBottom: 8 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Btn small icon={CheckCircle2} disabled={busy === r.id} onClick={() => confirm(r)}>
                            {busy === r.id ? '…' : 'Подтвердить'}
                          </Btn>
                          <Btn small color={C.danger} icon={XCircle} disabled={busy === r.id} onClick={() => reject(r)}>
                            Отклонить
                          </Btn>
                        </div>
                        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 8, lineHeight: 1.45 }}>
                          Подтверждение сразу активирует подписку и уведомит юзера в чате бота.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </Card>
      )}

      <Card title="Всего по статусам" icon={CreditCard} style={{ marginTop: 12 }}>
        {Object.entries(STATUS).map(([k, v]) => (
          <Row key={k} label={v.label} value={by[k] || 0} valueColor={v.color} />
        ))}
      </Card>
    </div>
  )
}
